import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ShoppingBag } from "@phosphor-icons/react";

const inr = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN")}`;

const getPriceDisplay = (product) => {
  const variants = product.variants || [];

  // If variant prices exist, calculate the actual price range
  const variantPrices = variants
    .map((v) => Number(v.price))
    .filter((price) => Number.isFinite(price) && price > 0);

  if (variantPrices.length > 0) {
    const minPrice = Math.min(...variantPrices);
    const maxPrice = Math.max(...variantPrices);

    if (minPrice === maxPrice) {
      return inr(minPrice);
    }

    return `${inr(minPrice)} – ${inr(maxPrice)}`;
  }

  // Fall back to the product's base price
  return inr(product.price);
};

export const ProductCard = ({ product, index = 0 }) => {
  const navigate = useNavigate();

  const variants = product.variants || [];

  // Product is sold out only when ALL variants are out of stock.
  const soldOut =
    variants.length > 0
      ? variants.every((v) => Number(v.stock || 0) <= 0)
      : product.in_stock === false;

  const handleAdd = (e) => {
    e.stopPropagation();

    // "Add to Bag" on the Shop page now takes the customer
    // to the Product Details page where they can select a size.
    navigate(`/product/${product.id}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{
        duration: 0.6,
        delay: (index % 4) * 0.06,
        ease: [0.16, 1, 0.3, 1],
      }}
      onClick={() => navigate(`/product/${product.id}`)}
      className="group border border-border -ml-px -mt-px bg-background flex flex-col cursor-pointer"
      data-testid={`product-card-${product.id}`}
    >
      {/* PRODUCT IMAGE */}
      <div className="aspect-[4/5] bg-secondary/60 relative overflow-hidden flex items-center justify-center">
        {product.image || product.images?.[0] ? (
          <img
            src={product.image || product.images[0]}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          />
        ) : (
          <div className="flex flex-col items-center gap-1 text-muted-foreground">
            <span className="font-serif italic text-lg">
              Uniform
            </span>
            <span className="text-[10px] uppercase tracking-[0.25em]">
              Co.
            </span>
          </div>
        )}

        {/* CATEGORY */}
        {product.category && (
          <span className="absolute top-3 left-3 text-[10px] uppercase tracking-widest bg-background/80 px-2 py-1">
            {product.category}
          </span>
        )}

        {/* OUT OF STOCK */}
        {soldOut && (
          <span className="absolute top-3 right-3 text-[10px] uppercase tracking-widest bg-foreground text-background px-2 py-1">
            Out of stock
          </span>
        )}
      </div>

      {/* PRODUCT INFORMATION */}
      <div className="p-4 flex flex-col gap-3 flex-1">

        {/* NAME + PRICE */}
        <div className="flex items-start justify-between gap-2">
          <h4 className="text-lg leading-tight">
            {product.name}
          </h4>

          <span className="text-sm whitespace-nowrap mt-1">
            {getPriceDisplay(product)}
          </span>
        </div>

        {/* SCHOOL */}
        {product.school_name && (
          <p className="text-xs text-muted-foreground">
            {product.school_name}
          </p>
        )}

        {/* 
          SIZE BUTTONS REMOVED FROM SHOP PAGE.
          Customers select their size on Product Details.
        */}

        {/* ADD TO BAG → PRODUCT DETAILS */}
        <button
          onClick={handleAdd}
          data-testid={`add-to-cart-${product.id}`}
          className="mt-auto w-full rounded-full border border-foreground py-2.5 text-xs uppercase tracking-widest hover:bg-foreground hover:text-background transition-colors duration-300 flex items-center justify-center gap-2"
        >
          <ShoppingBag size={15} />

          {soldOut ? "View Product" : "Add to Bag"}
        </button>
      </div>
    </motion.div>
  );
};

export default ProductCard;
